import { useState, useEffect } from "react";
import { getConstellation, addContribution } from "./supabaseClient";
import { useTheme, PageWrap, ThemeToggle, SERIF, SANS, PLAY, btnStyle, inputStyle as baseInputStyle, labelStyle } from "./theme";

var ADJECTIVES = [
  "Curious", "Creative", "Dedicated", "Outspoken", "Generous", "Loyal",
  "Funny", "Thoughtful", "Brave", "Kind", "Patient", "Driven",
  "Authentic", "Resilient", "Warm", "Fierce", "Gentle", "Bold",
  "Humble", "Passionate", "Reliable", "Inspiring", "Honest",
  "Adventurous", "Caring", "Confident", "Empathetic", "Determined",
  "Witty", "Protective", "Selfless", "Energetic", "Wise",
  "Supportive", "Charismatic", "Grounded",
];

var isValidEmail = function(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
};

export default function Contribute() {
  var { mode, t, toggle } = useTheme();
  var [constellation, setConstellation] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [phase, setPhase] = useState("intro"); // intro | memory | adjectives | joke | saving | done
  var [contributorName, setContributorName] = useState("");
  var [relationship, setRelationship] = useState("");
  var [otherRelationship, setOtherRelationship] = useState("");
  var [email, setEmail] = useState("");

  // Step data
  var [memory, setMemory] = useState("");
  var [selectedAdj, setSelectedAdj] = useState([]);
  var [insideJoke, setInsideJoke] = useState("");

  var slug = new URLSearchParams(window.location.search).get("c");

  useEffect(function() {
    if (!slug) { setError("No constellation link provided."); setLoading(false); return; }
    getConstellation(slug).then(function(data) {
      if (!data) { setError("Constellation not found."); }
      else { setConstellation(data); }
      setLoading(false);
    });
  }, [slug]);

  var canStart = function() {
    return contributorName.trim().length > 0 && isValidEmail(email);
  };

  var toggleAdj = function(adj) {
    setSelectedAdj(function(prev) {
      if (prev.indexOf(adj) !== -1) return prev.filter(function(a) { return a !== adj; });
      if (prev.length >= 6) return prev;
      return prev.concat([adj]);
    });
  };

  var submitContribution = async function() {
    setPhase("saving");
    try {
      var transcript = [{ role: "user", content: memory }];
      if (insideJoke.trim()) transcript.push({ role: "user", content: "Inside joke: " + insideJoke });
      var starLabel = selectedAdj.length > 0 ? selectedAdj[0].toLowerCase() : contributorName.toLowerCase();
      var finalRelationship = relationship === "other" && otherRelationship.trim() ? otherRelationship.trim() : relationship;
      await addContribution(constellation.id, {
        contributor_name: contributorName,
        relationship: finalRelationship,
        elements: selectedAdj.map(function(a) { return a.toLowerCase(); }),
        transcript: transcript,
        star_label: starLabel,
        email: email.trim().toLowerCase(),
      });
    } catch(e) { console.error(e); }
    setPhase("done");
  };

  var iStyle = baseInputStyle(t);
  var selectStyle = { ...iStyle, cursor: "pointer", color: relationship ? t.text : t.textMuted, appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23B8967A'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center" };
  var helperStyle = { fontFamily: SANS, fontSize: 11, fontWeight: 300, color: t.textMuted, lineHeight: 1.6, margin: "10px 2px 0", textAlign: "left" };

  if (loading) return (
    <PageWrap t={t}>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: t.textMuted, fontFamily: SANS, fontSize: 13, fontWeight: 300, letterSpacing: 2 }}>Loading&hellip;</span>
      </div>
    </PageWrap>
  );
  if (error) return (
    <PageWrap t={t}>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#C87070", fontFamily: SANS, fontSize: 14 }}>{error}</span>
      </div>
    </PageWrap>
  );

  return (
    <PageWrap t={t}>
      <style>{`
        @keyframes ytFadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes ytStarGrow { from { transform:scale(0) rotate(-90deg); opacity:0; } to { transform:scale(1) rotate(0deg); opacity:1; } }
        .yt-input:focus { border-color: ${t.accent} !important; }
        .yt-btn { transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94) !important; cursor: pointer !important; }
        .yt-btn:hover { transform: translateY(-2px) !important; }
      `}</style>
      <ThemeToggle t={t} toggle={toggle} mode={mode} />

      {/* ═══ INTRO ═══ */}
      {phase === "intro" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
          <div style={{ maxWidth: 440, textAlign: "center", animation: "ytFadeIn 0.8s ease-out" }}>
            <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, letterSpacing: 8, color: t.accentSoft, textTransform: "uppercase", marginBottom: 28 }}>Yours Truly</p>

            <h1 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(26px, 5vw, 36px)", fontStyle: "italic", color: t.text, margin: "0 0 16px", lineHeight: 1.3, letterSpacing: 1 }}>
              Add a star to<br />{constellation.recipient_name}'s constellation
            </h1>

            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 15, fontWeight: 300, color: t.textSoft, lineHeight: 1.9, marginBottom: 6 }}>
              {constellation.occasion && <span style={{ color: t.accent }}>{constellation.occasion} &middot; </span>}
              Someone is building a constellation for {constellation.recipient_name} — a map of who they are, made by the people who know them best.
            </p>
            <p style={{ fontFamily: SANS, fontSize: 12, fontWeight: 300, color: t.textMuted, lineHeight: 1.6, marginBottom: 44 }}>
              Takes about 2 minutes. Your words become a star.
            </p>

            <div style={{ display: "grid", gap: 16, maxWidth: 320, margin: "0 auto" }}>
              {!constellation.anonymous && (
                <>
                  <input
                    className="yt-input"
                    placeholder="Your first name"
                    value={contributorName}
                    onChange={function(e) { setContributorName(e.target.value); }}
                    onKeyDown={function(e) { if (e.key === "Enter" && canStart()) setPhase("memory"); }}
                    style={iStyle}
                  />
                  <select
                    className="yt-input"
                    value={relationship}
                    onChange={function(e) { setRelationship(e.target.value); }}
                    style={selectStyle}
                  >
                    <option value="" style={{ background: t.bg }}>How do you know them?</option>
                    <option value="friend" style={{ background: t.bg }}>Friend</option>
                    <option value="family" style={{ background: t.bg }}>Family</option>
                    <option value="colleague" style={{ background: t.bg }}>Colleague</option>
                    <option value="manager" style={{ background: t.bg }}>Manager</option>
                    <option value="direct report" style={{ background: t.bg }}>Direct report</option>
                    <option value="partner" style={{ background: t.bg }}>Partner</option>
                    <option value="other" style={{ background: t.bg }}>Other</option>
                  </select>
                  {relationship === "other" && (
                    <input
                      className="yt-input"
                      placeholder="How do you know them? (optional)"
                      value={otherRelationship}
                      onChange={function(e) { setOtherRelationship(e.target.value); }}
                      style={iStyle}
                    />
                  )}
                </>
              )}
              {constellation.anonymous && (
                <input
                  className="yt-input"
                  placeholder="Your first name (hidden on the constellation)"
                  value={contributorName}
                  onChange={function(e) { setContributorName(e.target.value); }}
                  onKeyDown={function(e) { if (e.key === "Enter" && canStart()) setPhase("memory"); }}
                  style={iStyle}
                />
              )}

              <div>
                <input
                  className="yt-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Your email"
                  value={email}
                  onChange={function(e) { setEmail(e.target.value); }}
                  onKeyDown={function(e) { if (e.key === "Enter" && canStart()) setPhase("memory"); }}
                  style={iStyle}
                />
                <p style={helperStyle}>
                  So we can send you your own copy of the finished poster.
                </p>
              </div>

              <button
                className="yt-btn"
                onClick={function() { setPhase("memory"); }}
                disabled={!canStart()}
                style={{
                  ...btnStyle(t),
                  width: "100%", padding: "18px 0", textAlign: "center",
                  opacity: canStart() ? 1 : 0.3,
                }}
              >
                Add my star
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STEP 1: MEMORY ═══ */}
      {phase === "memory" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
          <div style={{ maxWidth: 520, width: "100%", animation: "ytFadeIn 0.6s ease-out" }}>
            <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, letterSpacing: 6, color: t.accentSoft, textTransform: "uppercase", textAlign: "center", marginBottom: 12 }}>Step 1 of 3</p>
            <h2 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(22px, 4vw, 32px)", fontStyle: "italic", color: t.text, margin: "0 0 12px", lineHeight: 1.3, textAlign: "center" }}>
              Say something kind about {constellation.recipient_name}
            </h2>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, fontWeight: 300, color: t.textSoft, lineHeight: 1.8, textAlign: "center", marginBottom: 36, maxWidth: 400, margin: "0 auto 36px" }}>
              A memory, a moment, or what you'd say if someone handed you the mic. In your own words.
            </p>

            <textarea
              className="yt-input"
              placeholder={"What comes to mind when you think of " + constellation.recipient_name + "?"}
              value={memory}
              onChange={function(e) { setMemory(e.target.value); }}
              rows={6}
              style={{ ...iStyle, resize: "vertical", lineHeight: 1.8 }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
              <button
                className="yt-btn"
                onClick={function() { setPhase("adjectives"); }}
                disabled={!memory.trim()}
                style={{
                  ...btnStyle(t),
                  padding: "16px 44px",
                  opacity: memory.trim() ? 1 : 0.3,
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STEP 2: ADJECTIVES ═══ */}
      {phase === "adjectives" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
          <div style={{ maxWidth: 560, width: "100%", animation: "ytFadeIn 0.6s ease-out" }}>
            <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, letterSpacing: 6, color: t.accentSoft, textTransform: "uppercase", textAlign: "center", marginBottom: 12 }}>Step 2 of 3</p>
            <h2 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(22px, 4vw, 32px)", fontStyle: "italic", color: t.text, margin: "0 0 12px", lineHeight: 1.3, textAlign: "center" }}>
              What words describe {constellation.recipient_name}?
            </h2>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, fontWeight: 300, color: t.textSoft, lineHeight: 1.8, textAlign: "center", marginBottom: 36 }}>
              Pick up to 6 that feel right.
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 32 }}>
              {ADJECTIVES.map(function(adj) {
                var active = selectedAdj.indexOf(adj) !== -1;
                return (
                  <button
                    key={adj}
                    onClick={function() { toggleAdj(adj); }}
                    style={{
                      padding: "10px 20px",
                      background: active ? t.accentMuted : t.cardBg,
                      border: "1px solid " + (active ? t.accent : t.border),
                      color: active ? t.text : t.textSoft,
                      fontSize: 13, fontFamily: SANS, fontWeight: active ? 500 : 300,
                      cursor: "pointer", borderRadius: 20,
                      transition: "all 0.25s ease",
                      transform: active ? "scale(1.05)" : "scale(1)",
                    }}
                  >
                    {active && "✦ "}{adj}
                  </button>
                );
              })}
            </div>

            {selectedAdj.length > 0 && (
              <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 300, color: t.accentSoft, textAlign: "center", marginBottom: 24, letterSpacing: 1 }}>
                {selectedAdj.join(" · ")}{selectedAdj.length < 6 ? "" : " · max reached"}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="yt-btn"
                onClick={function() { setPhase("joke"); }}
                disabled={selectedAdj.length === 0}
                style={{
                  ...btnStyle(t),
                  padding: "16px 44px",
                  opacity: selectedAdj.length > 0 ? 1 : 0.3,
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STEP 3: INSIDE JOKE ═══ */}
      {phase === "joke" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
          <div style={{ maxWidth: 520, width: "100%", animation: "ytFadeIn 0.6s ease-out" }}>
            <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, letterSpacing: 6, color: t.accentSoft, textTransform: "uppercase", textAlign: "center", marginBottom: 12 }}>Step 3 of 3</p>
            <h2 style={{ fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(22px, 4vw, 32px)", fontStyle: "italic", color: t.text, margin: "0 0 12px", lineHeight: 1.3, textAlign: "center" }}>
              An inside joke, or a wish for them?
            </h2>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, fontWeight: 300, color: t.textSoft, lineHeight: 1.8, textAlign: "center", marginBottom: 36, maxWidth: 420, margin: "0 auto 36px" }}>
              Something that'd make them laugh, or something you hope for them. Totally optional.
            </p>

            <textarea
              className="yt-input"
              placeholder="That time at the lake house... / 'Don't touch my fries' / Or just what you hope for them"
              value={insideJoke}
              onChange={function(e) { setInsideJoke(e.target.value); }}
              rows={4}
              style={{ ...iStyle, resize: "vertical", lineHeight: 1.8 }}
            />

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
              <button
                onClick={submitContribution}
                style={{
                  ...btnStyle(t, "secondary"),
                  padding: "16px 32px",
                }}
              >
                Skip & Submit
              </button>
              <button
                className="yt-btn"
                onClick={submitContribution}
                style={{
                  ...btnStyle(t),
                  padding: "16px 44px",
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SAVING ═══ */}
      {phase === "saving" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, color: t.accent, marginBottom: 20, animation: "ytStarGrow 0.6s ease-out" }}>✦</div>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 17, fontWeight: 300, color: t.textSoft }}>Sealing your star&hellip;</p>
          </div>
        </div>
      )}

      {/* ═══ DONE ═══ */}
      {phase === "done" && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: "100vh", padding: 24, textAlign: "center",
        }}>
          <div style={{ animation: "ytFadeIn 0.8s ease-out" }}>
            <div style={{
              fontSize: 56, marginBottom: 24, lineHeight: 1,
              animation: "ytStarGrow 0.6s ease-out",
              color: t.accent,
            }}>✦</div>
            <h2 style={{
              fontFamily: PLAY, fontWeight: 400, fontSize: "clamp(26px, 5vw, 36px)",
              fontStyle: "italic", color: t.text, margin: "0 0 16px", lineHeight: 1.3,
            }}>
              Your star has been added
            </h2>
            <p style={{
              fontFamily: SERIF, fontStyle: "italic", fontSize: 15, fontWeight: 300,
              color: t.textSoft, lineHeight: 1.9, maxWidth: 380, margin: "0 auto",
            }}>
              Thank you, {contributorName}. Your words are now part of {constellation.recipient_name}'s constellation — a permanent point of light.
            </p>
            <a
              href={"/constellation?c=" + slug}
              className="yt-btn"
              style={{
                ...btnStyle(t, "secondary"),
                padding: "16px 36px",
                marginTop: 36,
              }}
            >
              See constellation in progress
            </a>
          </div>
        </div>
      )}
    </PageWrap>
  );
}
